/**
 * Renames dimensions whose label is owned by org meta rather than by the module.
 *
 * The customer dimensions are the case this exists for: `fleetName` and
 * `fleetCategory` are named by globalConfig.labels, so renaming the customer has
 * to rename the menu entry with it. Everything the override map does not name is
 * returned untouched, keeping fields a caller carries on its dimensions (apiKey,
 * shortLabel) intact.
 */
export const applyDimensionLabels = (dimensions = [], labelOverrides = {}) =>
  dimensions.map((dimension) =>
    labelOverrides[dimension.value]
      ? { ...dimension, label: labelOverrides[dimension.value] }
      : dimension
  );

/**
 * The breakdown dimensions a chart offers, from org meta where configured.
 *
 * Each module ships a built-in list. An org can replace it through org meta to
 * reorder the menu, drop dimensions that mean nothing to that customer, or
 * rename one. Configuring the block replaces the list rather than adding to it,
 * which is what makes removal possible; an absent or empty block keeps the
 * built-in list, so an org that configures nothing is unaffected.
 *
 * A configured value the module does not ship is dropped. Both the groupBy clause
 * builders and the filter builders on the server are if/elif chains with no else,
 * so an unknown dimension is silently ignored rather than rejected: the menu entry
 * would group by nothing and then drill into an unfiltered set. Better to leave it
 * out of the menu than to render something that quietly lies.
 */
export const resolveBreakdownDimensions = (configured, builtIn = [], labelOverrides = {}) => {
  const withLabels = (dimensions) => applyDimensionLabels(dimensions, labelOverrides);

  if (!Array.isArray(configured) || configured.length === 0) return withLabels(builtIn);

  const known = new Map(builtIn.map((dimension) => [dimension.value, dimension]));
  const resolved = configured
    .filter((dimension) => dimension?.value && known.has(dimension.value))
    .map((dimension) => ({
      value: dimension.value,
      // An explicit label in org meta wins, then the module's own, so a block that
      // only reorders does not have to restate every label.
      label: dimension.label || known.get(dimension.value).label,
    }));

  // A block naming nothing this module knows is a configuration mistake. Fall back
  // rather than rendering an empty menu the user cannot recover from.
  return withLabels(resolved.length ? resolved : builtIn);
};
